<template>
  <div
    ref="picker"
    class="ci-date-picker position-relative form-group">
    <div>
      <label :for="`ci-date_picker_${_uid}`" v-if="label">
        {{ label }}
      </label>

      <input
        maxlength="10"
        type="text"
        class="form-control w-100 bg-light"
        autocomplete="off"
        ref="inputField"
        :id="`ci-date_picker_${_uid}`"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="currentDate"
        @input="onInput"
        @keydown="onKeydown"
        @keydown.tab="onTab"
        @focus="onFocus">

      <div
        v-if="isOpen && !disabled"
        class="ci-date-picker--overlay position-absolute shadow bg-white">
        <div class="p-1 pt-3 d-flex flex-wrap">
          <div class="ci-date-picker__year d-flex w-100">
            <div
              v-if="canGoBack"
              class="toggle-year"
              @mousedown="preventSelection($event)"
              @click="monthDown()">
              <ci-awesome-icon icon="chevron-left" ratio="0.75" />
            </div>
            <h4 class="mt-0 mb-0 ml-auto mr-auto">
              {{ currentMonth.string }} {{ currentYear }}
            </h4>
            <div
              v-if="canGoForth"
              class="toggle-year"
              @mousedown="preventSelection($event)"
              @click="monthUp()">
              <ci-awesome-icon icon="chevron-right" ratio="0.75" />
            </div>
          </div>
          <hr>
          <div
            :class="{
              'ci-date-picker__day': true,
              'd-flex': true,
              'align-items-center': true,
              'justify-content-center': true,
              'today': day.isToday,
              'active': day.selected
              }"
            v-for="day in month"
            :key="`${day.iso}`"
            @click="onClickDay($event, day)">
            <span class="mt-1">
              {{ day.dayString }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import AwesomeIcon from '@/components/AwesomeIcon.vue';

export default {
  name: 'DatePicker',

  components: {
    'ci-awesome-icon': AwesomeIcon,
  },

  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    label: {
      type: String,
      default: '',
    },

    preSelectDate: {
      type: String,
      default: '',
      required: false,
    },

    preSelectFormat: {
      type: String,
      default: '',
      required: false,
    },

    limitToYear: {
      type: Number,
      required: false,
    },

    initialDate: {
      type: String,
      default: '',
      required: false,
    },
  },

  data() {
    return {
      currentMonth: null,
      currentYear: null,
      isOpen: false,
      dateFormat: '',
      moment: null,
      monthNum: null,
      placeholder: '',
      selectedDate: '',
      dividers: ['/', '.'],
    };
  },

  watch: {
    preSelectDate(newVal) {
      this.selectedDate = newVal;
      this.setDisplay();
    },
  },

  mounted() {
    this.moment = this.setupMoment();

    this.bindSubscription();

    this.moment.locale('de');
    this.dateFormat = this.moment.localeData().longDateFormat('L');

    this.setDisplay();

    this.documentClickProxy = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.documentClickProxy);

    this.translateDateFormat();
  },

  beforeDestroy() {
    if (this.destroySubscription) {
      this.destroySubscription();
    }
    if (this.documentClickProxy) {
      document.removeEventListener('click', this.documentClickProxy);
    }
  },

  computed: {
    canGoBack() {
      if (this.limitToYear && this.currentMonth.number === 0) {
        return false;
      }
      return true;
    },

    canGoForth() {
      if (this.limitToYear && this.currentMonth.number === 11) {
        return false;
      }
      return true;
    },

    currentDate() {
      if (this.selectedDate && this.selectedDate < 10) {
        return `${this.selectedDate}`;
      }

      if (this.selectedDate && this.selectedDate.length === 10) {
        return moment(this.selectedDate, 'YYYY-MM-DD').format(this.dateFormat);
      }

      return this.selectedDate;
    },

    month() {
      const daysInMonth = this.moment.daysInMonth();
      const selectedYear = this.moment.format('YYYY');
      const now = moment().format('YYYY-MM-DD');
      const month = [];
      const currentMonth = this.currentMonth.number + 1;
      const monthString =
        currentMonth.toString().length === 1
          ? `0${currentMonth.toString()}`
          : currentMonth.toString();
      for (let i = 1; i <= daysInMonth; i += 1) {
        const dayString = i.toString().length === 1 ? `0${i}` : i.toString();
        const iso = `${selectedYear}-${monthString}-${dayString}`;
        month.push({
          iso,
          dayString,
          isToday: now === iso,
          selected: this.selectedDate === iso,
        });
      }

      return month;
    },
  },

  methods: {
    bindSubscription() {
      this.destroySubscription = this.$store.subscribe((mutation) => {
        if (mutation.type === 'setLocale') {
          this.moment.locale(mutation.payload);
          this.dateFormat = this.moment.localeData().longDateFormat('L');
          this.translateDateFormat();
        }
      });
    },

    onDocumentClick(evt) {
      const { path } = evt;
      const filteredElement = path.filter(elem => elem === this.$refs.picker);
      if (!filteredElement.length) {
        this.toggleDatePicker(false);
      }
    },

    monthDown() {
      this.moment.subtract(1, 'months');
      if (this.limitToYear && this.moment.year() < this.limitToYear) {
        this.moment.add(1, 'months');
      }

      this.setDisplay();
    },

    monthUp() {
      this.moment.add(1, 'months');
      if (this.limitToYear && this.moment.year() > this.limitToYear) {
        this.moment.substract(1, 'months');
      }
      this.setDisplay();
    },

    preventSelection(evt) {
      evt.preventDefault();
    },

    cleanInput(input) {
      const splittedInput = input.split('');
      const output = [];
      for (let i = 0; i < splittedInput.length; i += 1) {
        const currentChar = splittedInput[i];
        if (this.isDivider(currentChar)) {
          output.push(this.getDivider());
        } else {
          output.push(currentChar);
        }
      }

      return output.join('');
    },

    onFocus() {
      this.toggleDatePicker(true);
    },

    parseDate(value) {
      const cleanedValue = this.cleanInput(value);

      const divider = this.getDivider();
      const splittedDate = cleanedValue.split(divider);
      const splittedDateFormat = this.dateFormat.split(divider);
      const lastCharIsDivider =
        cleanedValue[cleanedValue.length - 1] === divider;

      const dates = {
        DD: '',
        MM: '',
        YYYY: '',
      };

      for (let i = 0; i < splittedDate.length; i += 1) {
        const requiredLength = splittedDateFormat[i].length;
        const currentInputType = splittedDateFormat[i];
        const splittedDateArr = splittedDate[i].split('');

        if (splittedDateArr.length > requiredLength) {
          const diff = splittedDateArr.length - requiredLength;
          splittedDateArr.splice(requiredLength, diff);
        }

        if (
          splittedDateArr.length < requiredLength &&
          lastCharIsDivider &&
          i < 3
        ) {
          splittedDateArr.unshift(0);
        }

        if (splittedDateArr.length === requiredLength) {
          dates[currentInputType] = splittedDateArr.join('');
        }
      }

      const output = [];
      for (let i = 0; i < splittedDateFormat.length; i += 1) {
        if (
          dates[splittedDateFormat[i]] &&
          dates[splittedDateFormat[i]].length
        ) {
          output.push(dates[splittedDateFormat[i]]);
        }
      }

      if (output.length === 1 || output.length === 2) {
        output[output.length - 1] = `${output[output.length - 1]}${divider}`;
      }

      return {
        currentDate: output.join(divider),
        iso: `${dates.YYYY}-${dates.MM}-${dates.DD}`,
        day: dates.DD,
        month: dates.MM - 1,
        year: dates.YYYY,
      };
    },

    isNumeric(evt) {
      const keyCode = evt.which || evt.keyCode;
      if (evt.shiftKey) {
        return false;
      }

      if (
        // eslint-disable-next-line
        isNaN(String.fromCharCode(keyCode)) &&
        keyCode !== 8 &&
        keyCode !== 46 &&
        keyCode !== 37 &&
        keyCode !== 39
      ) {
        return false;
      }

      return true;
    },

    onKeydown(evt) {
      const { key } = evt;
      const inputValue = this.$refs.inputField.value;
      const lastCharIsDivider = this.isDivider(inputValue[inputValue.length - 1]);
      const newCharIsDivider = this.isDivider(key);

      if (
        (lastCharIsDivider && newCharIsDivider) ||
        (!this.isNumeric(evt) && !this.isDivider(key))
      ) {
        evt.preventDefault();
      }
    },

    onInput() {
      const inputValue = this.$refs.inputField.value;
      const { selectedDate } = this;
      const parsedDate = this.parseDate(inputValue);

      if (inputValue.length >= selectedDate.length && inputValue.length < 10) {
        this.$emit('date-deselected', {
          key: this.name,
          data: null,
        });
        this.selectedDate = parsedDate.currentDate;
      }

      if (
        inputValue.length === 10 &&
        moment(parsedDate.iso, 'YYYY-MM-DD').isValid()
      ) {
        this.$emit('date-selected', {
          key: this.name,
          data: parsedDate.iso,
        });
        this.moment.set('year', parsedDate.year);
        this.moment.set('month', parsedDate.month);
        this.moment.set('date', parsedDate.day);
        this.selectedDate = parsedDate.iso;
        this.setDisplay();
      }

      if (inputValue.length < selectedDate.length) {
        this.$emit('date-deselected', {
          key: this.name,
          data: null,
        });
        this.selectedDate = inputValue;
      }
    },

    isDivider(key) {
      const index = this.dividers.indexOf(key);
      if (index === -1) {
        return false;
      }

      return true;
    },

    getDivider() {
      const dateFormatArr = this.dateFormat.split('');
      for (let i = 0; i < dateFormatArr.length; i += 1) {
        if (
          dateFormatArr[i] !== 'D' &&
          dateFormatArr[i] !== 'M' &&
          dateFormatArr[i] !== 'Y'
        ) {
          return dateFormatArr[i];
        }
      }

      return null;
    },

    onTab() {
      this.toggleDatePicker(false);
    },

    toggleDatePicker(state) {
      this.isOpen = state;
    },

    onClickDay(evt, daySelected) {
      evt.stopPropagation();
      if (this.selectedDate && this.selectedDate === daySelected.iso) {
        this.selectedDate = null;
        this.$emit('date-picker:date-selected', {
          data: null,
        });
      } else {
        this.selectedDate = daySelected.iso;
        this.$emit('date-picker:date-selected', {
          data: daySelected.iso,
        });
      }
      this.toggleDatePicker(false);
    },

    setDisplay() {
      this.currentMonth = {
        number: this.moment.month(),
        string: this.moment.format('MMMM'),
      };

      this.currentYear = this.moment.format('YYYY');
    },

    setupMoment() {
      const { preSelectDate } = this;
      if (preSelectDate && moment(preSelectDate, 'YYYY-MM-DD').isValid()) {
        this.selectedDate = preSelectDate;
        return moment(preSelectDate, 'YYYY-MM-DD');
      }

      const { initialDate } = this;
      if (initialDate && moment(initialDate, 'YYYY-MM-DD').isValid()) {
        return moment(this.initialDate, 'YYYY-MM-DD');
      }
      return moment();
    },

    translateDateFormat() {
      let splittedDateFormat = this.dateFormat.split('');
      splittedDateFormat = splittedDateFormat.map((date) => {
        if (date === 'D' || date === 'M' || date === 'Y') {
          // return this.$t(`abbreviations.${date}`);
        }
        return date;
      });

      this.placeholder = splittedDateFormat.join('');
    },
  },
};
</script>
